import React, { useState, useEffect } from 'react';
import { Card, CardBody } from 'reactstrap';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useSelector, useDispatch } from 'react-redux';
import * as subscriptionsActions from '../../../store/subscriptions/actions';
import CheckoutForm from '../PaymentStripe/CheckoutForm';
import { trackSubscription } from '../../../lib/analytics';
import { trackAffiliateConversion } from '../../../helpers/affiliateTracking';
import colors from '../../CommonStyle/colors';
import { eurosToCents } from '../../../helpers/priceUtils';

const PaymentStep = ({ plan, appliedDiscount, onPrevious }) => {
  const userId = useSelector((state) => state.Auth.admin.id);
  const loading = useSelector((state) => state.Subscription.loading);

  const [stripePromise, setStripePromise] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Helper function to parse price (same as in CreateSubscription)
  const parsePrice = (priceString) => {
    if (typeof priceString === 'number') return priceString;
    if (typeof priceString === 'string') {
      return parseFloat(priceString.replace(',', '.'));
    }
    return 0;
  };

  // Calculate final price considering discount
  const originalPriceString = plan.promoPrice || plan.price;
  const originalPrice = parsePrice(originalPriceString);
  const finalPrice = appliedDiscount ? appliedDiscount.newPrice : originalPrice;
  const isFree = appliedDiscount && appliedDiscount.isFree;
  const savings = appliedDiscount ? originalPrice - finalPrice : 0;

  useEffect(() => {
    setStripePromise(loadStripe(process.env.REACT_APP_STRIPE_KEY));
  }, []);

  const dispatch = useDispatch();

  const handleUserSubscription = () => {
    dispatch(subscriptionsActions.fetchSubscriptionsByUser(userId));
  };

  const handleOnPaymentSuccess = () => {
    setPaymentSuccess(true);

    // Track affiliate conversion if applicable
    const subscriptionData = {
      userId,
      plan: plan.title,
      amount: parseFloat(finalPrice),
      currency: 'EUR',
      period: plan.period,
    };

    trackAffiliateConversion(subscriptionData);

    trackSubscription(subscriptionData);
  };

  const options = {
    mode: 'subscription',
    currency: 'eur',
    amount: eurosToCents(finalPrice),
    appearance: {
      theme: 'stripe',
      variables: {
        fontSizeBase: '1.05rem',
        colorText: colors.text,
        colorPrimary: colors.primary,
        colorBackground: colors.white,
        colorWarningText: colors.warning,
        colorDanger: colors.danger,
        colorSuccess: colors.success,
        fontFamily: "'Noto Sans JP', sans-serif",
      },
    },
  };

  return (
    <div className="step-content">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="text-center mb-4">
            <h3 className={`mb-2 ${paymentSuccess ? 'text-success' : ''}`}>
              {!paymentSuccess
                ? 'Completa il pagamento'
                : 'Complimenti! Abbonamento attivato'}
            </h3>
            <p className="text-muted">
              {!paymentSuccess
                ? 'Inserisci i dettagli di pagamento per attivare il tuo piano'
                : 'Hai completato con successo la sottoscrizione del tuo piano'}
            </p>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-md-8">
          {/* Order Summary */}
          <Card className="mb-4 border-light">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="mdi mdi-receipt mr-2"></i>
                Riepilogo ordine
              </h5>
            </div>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h6 className="mb-1">{plan.title}</h6>
                  <small className="text-muted">{plan.subtitle}</small>
                </div>
                <div className="text-right">
                  {appliedDiscount && !isFree && (
                    <div className="text-muted text-decoration-line-through small">
                      €{originalPrice.toFixed(2)}
                    </div>
                  )}
                  <strong>
                    {isFree ? (
                      <span className="text-success">Gratuito</span>
                    ) : (
                      `€${finalPrice.toFixed(2)}`
                    )}
                  </strong>
                </div>
              </div>

              {appliedDiscount && (
                <div className="d-flex justify-content-between align-items-center mb-2 text-success">
                  <small>
                    <i className="bx bx-check-circle mr-1"></i>
                    Codice sconto "{appliedDiscount.code}"
                  </small>
                  <small>
                    {isFree ? 'Piano gratuito' : `-€${savings.toFixed(2)}`}
                  </small>
                </div>
              )}

              <hr className="my-2" />

              <div className="d-flex justify-content-between align-items-center">
                <strong>Totale {!isFree && `/ ${plan.periodLabel}`}</strong>
                <strong className={isFree ? 'text-success' : 'text-primary'}>
                  {isFree ? 'Gratuito' : `€${finalPrice.toFixed(2)}`}
                </strong>
              </div>
            </CardBody>
          </Card>

          {/* Payment Information */}
          {!paymentSuccess && (
            <div className="p-3 bg-light rounded mb-4">
              <h6 className="mb-2">Perché chiediamo queste informazioni?</h6>
              <p className="font-size-13 text-muted mb-0">
                I dati sono necessari per completare il primo pagamento e darti
                accesso alla piattaforma.
              </p>
              <p className="font-size-13 text-muted mb-0 mt-2">
                <a
                  href="https://firebasestorage.googleapis.com/v0/b/sos-dottore.firebasestorage.app/o/contratti%2FInformazioni%20Generali%20e%20Condizioni%20di%20servizio%20DOC24%20BASE_MDF_180_def.pdf?alt=media&token=b37c082f-ac7c-49f2-ad8c-196337be7e94"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary">
                  Visualizza Informazioni generali e Condizioni di Servizio
                </a>
              </p>
            </div>
          )}

          {/* Payment Form or Success Message */}
          {!paymentSuccess ? (
            <Card>
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <div className="mr-3">
                    <i className="mdi mdi-credit-card text-primary h2 mb-0" />
                  </div>
                  <div>
                    <h5 className="mb-1">Dettagli di pagamento</h5>
                    <p className="text-muted mb-0">
                      {isFree
                        ? 'Clicca su "Attiva Piano" per completare l\'attivazione'
                        : 'Inserisci i dettagli della tua carta di credito'}
                    </p>
                  </div>
                </div>

                <Elements stripe={stripePromise} options={options}>
                  <CheckoutForm
                    endpoint="/stripe/create-subscription"
                    onPaymentSuccess={handleOnPaymentSuccess}
                    data={{
                      userId,
                      plan: {
                        stripeId: plan.stripeId,
                        period: plan.period,
                      },
                      ...(appliedDiscount && { discountCode: appliedDiscount }),
                    }}
                  />
                </Elements>
              </CardBody>
            </Card>
          ) : (
            <Card className="border-success">
              <CardBody className="text-center p-4">
                <div className="avatar-md mx-auto mb-4">
                  <span className="avatar-title rounded-circle bg-success">
                    <i className="bx bx-check font-size-24 text-white"></i>
                  </span>
                </div>
                <h4 className="text-success mb-3">Abbonamento attivato!</h4>
                <p className="text-muted mb-4 font-size-16">
                  Ora puoi accedere a tutti i servizi di SOS Dottore. Riceverai
                  a breve le tue credenziali via SMS e Email per utilizzare
                  l'app Doc24.
                </p>
                <button
                  type="button"
                  className="btn btn-success btn-rounded waves-effect waves-light px-4"
                  onClick={() => handleUserSubscription()}
                  disabled={loading}>
                  <i
                    className={`${
                      loading ? 'bx bx-spin' : 'bx bx-user'
                    } align-middle mr-2 font-size-18`}></i>
                  Accedi ai tuoi servizi
                </button>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Navigation Buttons - Only show if payment not successful */}
      {!paymentSuccess && (
        <div className="row justify-content-center mt-4">
          <div className="col-12 col-md-8">
            <div className="twitter-bs-wizard-pager-link d-flex justify-content-start mb-5">
              <button
                type="button"
                className="btn btn-outline-secondary btn-rounded waves-effect waves-light px-4"
                onClick={onPrevious}>
                <i className="bx bx-chevron-left mr-2"></i>
                Indietro
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentStep;
